/**
 * Navigation Component
 * Main navigation menu with rocket animation
 */

import { Link, useLocation } from 'react-router-dom'
import { useState, useEffect, useRef } from 'react'
import BackButton from './BackButton'

const MENU_ITEMS = [
  { text: 'Home', path: '/', shortText: 'Home' },
  { text: 'Countdown', path: '/countdown', shortText: 'Countdown' },
  { text: 'Blastoff!', path: '/blastoff', shortText: 'Blastoff!' },
  { text: 'Shooting Through Space', path: '/space', shortText: 'Space' },
  { text: 'Landing on the Moon', path: '/moon', shortText: 'Moon' },
  { text: 'Shoot to Mars', path: '/mars', shortText: 'Mars' },
  { text: 'Practice Station', path: '/practice', shortText: 'Practice' },
  { text: 'About', path: '/about', shortText: 'About' }
]

const MENU_ID = 'main-navigation-menu'

// Safe localStorage helper
function getStoredMenuState(): boolean {
  if (typeof window === 'undefined') return false
  try {
    return localStorage.getItem('menuOpen') === 'true'
  } catch {
    return false
  }
}

function setStoredMenuState(isOpen: boolean): void {
  if (typeof window === 'undefined') return
  try {
    localStorage.setItem('menuOpen', String(isOpen))
  } catch {
    // Fail silently in private mode/SSR
  }
}

export default function Navigation() {
  const [isMenuOpen, setIsMenuOpen] = useState(getStoredMenuState)
  const location = useLocation()
  const menuRef = useRef<HTMLUListElement>(null)

  // Persist menu state to localStorage
  useEffect(() => {
    setStoredMenuState(isMenuOpen)
  }, [isMenuOpen])

  // Close menu on navigation
  useEffect(() => {
    setIsMenuOpen(false)
  }, [location.pathname])

  // Handle Escape key and click outside
  useEffect(() => {
    if (!isMenuOpen) return

    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        setIsMenuOpen(false)
      }
    }

    const handleClickOutside = (e: MouseEvent) => {
      const target = e.target as Node
      if (menuRef.current && !menuRef.current.contains(target)) {
        // Check if click was on the toggle button
        const toggleButton = document.querySelector('.mobile-menu-btn')
        if (toggleButton && !toggleButton.contains(target)) {
          setIsMenuOpen(false)
        }
      }
    }

    document.addEventListener('keydown', handleEscape)
    document.addEventListener('mousedown', handleClickOutside)

    return () => {
      document.removeEventListener('keydown', handleEscape)
      document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [isMenuOpen])

  const toggleMenu = () => {
    // Use functional update to avoid stale state
    setIsMenuOpen(prev => !prev)
  }

  return (
    <nav>
      <div className="menu-container">
        <BackButton />

        <div className="menu-right-group">
          <button
            className="mobile-menu-btn"
            onClick={toggleMenu}
            aria-label={isMenuOpen ? 'Close menu' : 'Open menu'}
            aria-expanded={isMenuOpen}
            aria-controls={MENU_ID}
          >
            {isMenuOpen ? '✕' : '☰'}
          </button>

          <ul
            id={MENU_ID}
            ref={menuRef}
            className={`nav-links ${isMenuOpen ? 'show' : ''}`}
          >
            {MENU_ITEMS.map(item => {
              const isActive = location.pathname === item.path
              return (
                <li key={item.path}>
                  <Link
                    to={item.path}
                    className={isActive ? 'active' : ''}
                    aria-current={isActive ? 'page' : undefined}
                  >
                    <span className="full-text">{item.text}</span>
                    <span className="short-text">{item.shortText}</span>
                  </Link>
                </li>
              )
            })}
            <li className="rocket-wrapper" aria-hidden="true">
              <img
                src="/images/icons/rocket.gif"
                className="rocket-icon"
                alt=""
              />
            </li>
          </ul>
        </div>
      </div>
    </nav>
  )
}
